import { uniq } from 'lodash-es';
import { contentsSearchParams, nonNullable } from '~/utils';
import {
  ArticleCard,
  RelatedMagazinesReturnValue,
  RelatedEventsReturnValue,
  RelatedProducerArticle,
} from '~/types/modules/contents';
import { ArticleType } from '~/enums';
import { useTags } from './useTags';
import { useStore } from '../useStore';

export const useRelatedContents = () => {
  const store = useStore();
  const { allTagNames } = useTags();

  const getRelatedSpecialFeatures = async (ids: number[]): Promise<ArticleCard[] | undefined> => {
    try {
      const articleIds = ids.map((id) => `article_${id}`);
      const { q } = contentsSearchParams({
        articleType: ArticleType.Archives,
        articleIds: `${articleIds.join(' OR ')}`,
      });
      const { articles } = await store.dispatch('modules/content/fetchArticles', { q });
      return articles;
    } catch {
      // 何にもしない
    }
  };

  const getRelatedMagazines = async (ids: number[]): Promise<RelatedMagazinesReturnValue | undefined> => {
    try {
      const articles = await store.dispatch('modules/content/fetchRelatedMagazines', {
        articleIdList: ids,
      });
      const categories = await store.dispatch('modules/content/fetchArticleCategories', {});
      const authorIds = articles
        .filter((article: ArticleCard) => article.authorId)
        .map((article: ArticleCard) => Number(article.authorId));
      const filteredAuthorIds: number[] = uniq(authorIds);
      const authors = await store.dispatch('modules/content/fetchAuthors', {
        authorIds: filteredAuthorIds,
      });
      return {
        articles,
        categories,
        authors,
      };
    } catch {
      // 何にもしない
    }
  };

  const getRelatedProducers = async (ids: number[]): Promise<RelatedProducerArticle[] | undefined> => {
    try {
      const { articles } = await store.dispatch('modules/content/fetchProducers', {
        articleIds: ids,
      });
      const producerIds = articles.map((article) => article.permalink);
      const { producers } = await store.dispatch('modules/content/fetchSortedProducers', {
        //  カタカナ順
        sort: 2,
        rows: 100,
        producerIds,
      });
      return producers.filter(nonNullable);
    } catch {
      // 何にもしない
    }
  };

  const getRelatedEvents = async (ids: number[]): Promise<RelatedEventsReturnValue | undefined> => {
    try {
      const query = { ids };
      const res = await store.dispatch('event/list/fetchEvents', { query });
      const mappedTags = await allTagNames(res.items);
      return {
        events: res.items || [],
        tags: mappedTags,
      };
    } catch {
      // 何にもしない
    }
  };

  return {
    getRelatedSpecialFeatures,
    getRelatedMagazines,
    getRelatedProducers,
    getRelatedEvents,
  };
};
